// src/websocket/websocket-utils.ts
import { fetchAuthSession } from '@aws-amplify/auth'

type MessageHandler = (msg: any) => void

export class WebSocketClient {
  private socket: WebSocket | null = null
  private token: string | null = null
  private handlers: MessageHandler[] = []
  private heartbeatInterval: any
  private reconnectDelay = 1000 // start with 1 second
  private maxReconnectDelay = 30000
  private reconnectTimeout: any = null
  private manuallyClosed = false

  constructor(private readonly url: string) {}

  async connect(): Promise<void> {
    this.manuallyClosed = false
    this.token = await this.getIdToken()
    const wsUrl = `${this.url}?token=${encodeURIComponent(this.token)}`
    this.socket = new WebSocket(wsUrl)

    this.socket.onopen = () => {
      console.log('[WS] Connected')
      this.startHeartbeat()
      this.clearReconnectBackoff()
    }

    this.socket.onclose = (event) => {
      console.warn('[WS] Disconnected', event.code, event.reason)
      this.stopHeartbeat()
      if (!this.manuallyClosed) {
        this.scheduleReconnect()
      }
    }

    this.socket.onerror = (err) => console.error('[WS] Error', err)

    this.socket.onmessage = (msg) => {
      try {
        const data = JSON.parse(msg.data)
        this.handlers.forEach(fn => fn(data))
      } catch (err) {
        console.warn('[WS] Invalid message:', msg.data)
      }
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimeout) return
    this.reconnectTimeout = setTimeout(() => {
      console.log('[WS] Attempting to reconnect...')
      this.reconnectTimeout = null
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay)
      this.connect()
    }, this.reconnectDelay)
  }

  private clearReconnectBackoff() {
    this.reconnectDelay = 1000
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
  }

  private startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ type: 'ping' }))
      }
    }, 30000) // every 30s
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  send(data: any): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(data))
    } else {
      console.warn('[WS] Not connected — cannot send')
    }
  }

  onMessage(handler: MessageHandler): void {
    this.handlers.push(handler)
  }

  disconnect(): void {
    this.manuallyClosed = true
    this.stopHeartbeat()
    this.clearReconnectBackoff()
    this.socket?.close()
  }

  private async getIdToken(): Promise<string> {
    const session = await fetchAuthSession()
    return session.tokens?.idToken?.toString() || ''
  }
}

